import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { useXPGainToasts } from "@/hooks/useXPGainToasts";
import { useCreditResetReminder } from "@/hooks/useCreditResetReminder";
import LevelUpCelebration from "@/components/LevelUpCelebration";
import Index from "./pages/Index.tsx";
import MobileBottomNav from "./components/MobileBottomNav.tsx";
import CompareTrayBar from "./components/compare/CompareTrayBar.tsx";
import ReportBugButton from "./components/ReportBugButton.tsx";
import AskTutorFAB from "./components/tutor/AskTutorFAB.tsx";
import ErrorBoundary from "./components/ErrorBoundary.tsx";
import { OnboardingModal } from "./components/onboarding/OnboardingModal.tsx";
import { CompareTrayProvider } from "./hooks/useCompareTray";
import AnnouncementBanner from "./components/AnnouncementBanner.tsx";
import MaintenanceGate from "./components/MaintenanceGate.tsx";
import OperatorMilestoneModal from "./components/play/OperatorMilestoneModal.tsx";
import Level2PaywallGate from "./components/Level2PaywallGate.tsx";
import FirstOutputSignupGate from "./components/FirstOutputSignupGate.tsx";
import FirstVisitSignupModal from "./components/FirstVisitSignupModal.tsx";
import BeginnerShellWithBoundary from "./components/beginner/BeginnerShellWithBoundary.tsx";
import AgentUnlockModal from "./components/beginner/AgentUnlockModal.tsx";
import FirstAgentPeekModal from "./components/beginner/FirstAgentPeekModal.tsx";
import FirstExitCoachmarks from "./components/beginner/FirstExitCoachmarks.tsx";

// Code-split every non-landing route so the initial bundle drops from ~412KB
// to roughly the size of Index + shared deps. Lighthouse mobile flagged
// 253KB of unused JS; the heavy offenders (Hub, Play, Workshop, Admin)
// were all bundled into the entry chunk despite never running on first paint.
const Settings = lazy(() => import("./pages/Settings.tsx"));
const ToolDetail = lazy(() => import("./pages/ToolDetail.tsx"));
const HackerGuide = lazy(() => import("./pages/HackerGuide.tsx"));
const SharedGuide = lazy(() => import("./pages/SharedGuide.tsx"));
const SharedResearch = lazy(() => import("./pages/SharedResearch.tsx"));
const SharedChat = lazy(() => import("./pages/SharedChat.tsx"));
const Go = lazy(() => import("./pages/Go.tsx"));
const Hub = lazy(() => import("./pages/Hub.tsx"));
const AffiliateDisclosure = lazy(() => import("./pages/AffiliateDisclosure.tsx"));
const AdminAffiliates = lazy(() => import("./pages/AdminAffiliates.tsx"));
const AdminTools = lazy(() => import("./pages/AdminTools.tsx"));
const AdminFeedback = lazy(() => import("./pages/AdminFeedback.tsx"));
const AdminErrors = lazy(() => import("./pages/AdminErrors.tsx"));
const AdminSite = lazy(() => import("./pages/AdminSite.tsx"));
const AdminFunnel = lazy(() => import("./pages/AdminFunnel.tsx"));
const AdminBilling = lazy(() => import("./pages/AdminBilling.tsx"));
const AdminProjectSkills = lazy(() => import("./pages/AdminProjectSkills.tsx"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics.tsx"));
const AdminLighthouse = lazy(() => import("./pages/AdminLighthouse.tsx"));
const AdminAbuse = lazy(() => import("./pages/AdminAbuse.tsx"));
const AdminSecurity = lazy(() => import("./pages/AdminSecurity.tsx"));
const AdminSecurityIssues = lazy(() => import("./pages/AdminSecurityIssues.tsx"));
const Builder = lazy(() => import("./pages/Builder.tsx"));
const PublishedSite = lazy(() => import("./pages/PublishedSite.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const Feedback = lazy(() => import("./pages/Feedback.tsx"));
const Enterprise = lazy(() => import("./pages/Enterprise.tsx"));
const Security = lazy(() => import("./pages/Security.tsx"));

const About = lazy(() => import("./pages/About.tsx"));
const UsageLimits = lazy(() => import("./pages/UsageLimits.tsx"));
const Conceptually = lazy(() => import("./pages/Conceptually.tsx"));
const Accelerator = lazy(() => import("./pages/Accelerator.tsx"));
const Legal = lazy(() => import("./pages/Legal.tsx"));
const Pricing = lazy(() => import("./pages/Pricing.tsx"));
const Account = lazy(() => import("./pages/Account.tsx"));
const SharedCollection = lazy(() => import("./pages/SharedCollection.tsx"));
const Play = lazy(() => import("./pages/Play.tsx"));
const PlayPath = lazy(() => import("./pages/PlayPath.tsx"));
const QuestWorkshopPage = lazy(() => import("./pages/QuestWorkshopPage.tsx"));
const Leaderboard = lazy(() => import("./pages/Leaderboard.tsx"));
const Invite = lazy(() => import("./pages/Invite.tsx"));
const InviteDashboard = lazy(() => import("./pages/InviteDashboard.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const InviteAccept = lazy(() => import("./pages/InviteAccept.tsx"));
const Signup = lazy(() => import("./pages/Signup.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Compare = lazy(() => import("./pages/Compare.tsx"));
const Directory = lazy(() => import("./pages/Directory.tsx"));
const ToolsList = lazy(() => import("./pages/ToolsList.tsx"));
const Squads = lazy(() => import("./pages/Squads.tsx"));
const SquadDetail = lazy(() => import("./pages/SquadDetail.tsx"));
const SquadJoin = lazy(() => import("./pages/SquadJoin.tsx"));
const SquadRooms = lazy(() => import("./pages/SquadRooms.tsx"));
const SquadRoom = lazy(() => import("./pages/SquadRoom.tsx"));
const Library = lazy(() => import("./pages/Library.tsx"));
const LibraryRoadmap = lazy(() => import("./pages/LibraryRoadmap.tsx"));
const LibraryArtifact = lazy(() => import("./pages/LibraryArtifact.tsx"));
const Challenges = lazy(() => import("./pages/Challenges.tsx"));
const Mastery = lazy(() => import("./pages/Mastery.tsx"));
const MasteryHistory = lazy(() => import("./pages/MasteryHistory.tsx"));
const Power21 = lazy(() => import("./pages/Power21.tsx"));
const MasterChallenges = lazy(() => import("./pages/MasterChallenges.tsx"));
const Cert = lazy(() => import("./pages/Cert.tsx"));
const Projects = lazy(() => import("./pages/Projects.tsx"));
const ProjectsLeaderboard = lazy(() => import("./pages/ProjectsLeaderboard.tsx"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail.tsx"));
const Agents = lazy(() => import("./pages/Agents.tsx"));
const AgentsDashboard = lazy(() => import("./pages/AgentsDashboard.tsx"));
const Hackathon = lazy(() => import("./pages/Hackathon.tsx"));
const AdminHackathon = lazy(() => import("./pages/AdminHackathon.tsx"));
const Planner = lazy(() => import("./pages/Planner.tsx"));

const queryClient = new QueryClient();

// Minimal route fallback — a full-bleed background that matches `bg-background`
// so there's no white flash while the chunk fetches. Intentionally empty
// (no spinner) since chunks land in <100ms on a warm cache.
const RouteFallback = () => <div className="min-h-screen bg-background" aria-hidden="true" />;

// Legacy URL redirects. Routes have been renamed several times as the
// IA settled (/guide → /learn, /hub → /hack → /workspace, /play →
// /build → /quests, /mastery → /roadmap, /master-challenges →
// /challenges/mastery, /library/roadmap → /library/wishlist). These
// client-side redirects keep old shared links working. They're
// SPA-level (not real 301s — Lovable hosting can't issue server
// redirects), but they preserve the slug/path so users land in the
// right place on the new route.
const RedirectGuideToLearn = () => {
  const { slug } = useParams();
  return <Navigate to={slug ? `/learn/${slug}` : "/learn"} replace />;
};
// /hub and /hack both now point at /workspace. We keep two redirect
// components so the breadcrumb of renames stays readable. Preserve
// the search + hash so deep links like `/hub?tab=chat` (used by the
// Beginner Mode "Test it now" CTA) actually land on the right tab
// instead of dropping the param and looking broken.
const RedirectHubToWorkspace = () => (
  <Navigate
    to={{ pathname: "/workspace", search: window.location.search, hash: window.location.hash }}
    replace
  />
);
const RedirectHackToWorkspace = () => (
  <Navigate
    to={{ pathname: "/workspace", search: window.location.search, hash: window.location.hash }}
    replace
  />
);
// /play and /build both now point at /quests. Preserve any sub-path
// (e.g. /play/paths/foo or /build/paths/foo/quests/bar).
const RedirectLegacyToQuests = () => {
  const params = useParams();
  const rest = params["*"];
  return <Navigate to={rest ? `/quests/${rest}` : "/quests"} replace />;
};
// /mastery was renamed to /roadmap. Preserve the query string + hash so
// deep-links like /mastery?launch=3 and /mastery#skill-2 keep working.
const RedirectMasteryToRoadmap = () => (
  <Navigate
    to={{ pathname: "/roadmap", search: window.location.search, hash: window.location.hash }}
    replace
  />
);
// /master-challenges → /challenges/mastery (re-homed under /challenges).
const RedirectMasterChallenges = () => (
  <Navigate to="/challenges/mastery" replace />
);
// /library/roadmap → /library/wishlist (it's a community wishlist, not
// a roadmap — and the new top-level /roadmap is a different concept).
const RedirectLibraryRoadmap = () => (
  <Navigate to="/library/wishlist" replace />
);

// Tiny wrapper component so we can call the points-gain hook inside the auth
// provider tree (hooks need React context). Renders the global toasters.
const GlobalToasters = () => {
  useXPGainToasts();
  // Once-per-month "credits refilled" reminder. Toast for everyone,
  // browser notification for users who've granted the permission via
  // /settings. Idempotent — won't re-fire on reload within the same
  // reset cycle.
  useCreditResetReminder();
  return (
    <>
      <Toaster />
      <Sonner />
      <LevelUpCelebration />
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <AuthProvider>
      <TooltipProvider>
        <GlobalToasters />
        <BrowserRouter>
          <CompareTrayProvider>
          <AnnouncementBanner />
          <MaintenanceGate>
          <Suspense fallback={<RouteFallback />}>
          <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/directory" element={<Directory />} />
            <Route path="/tools" element={<ToolsList />} />
            <Route path="/tool/:slug" element={<ToolDetail />} />
            <Route path="/learn" element={<HackerGuide />} />
            <Route path="/learn/:slug" element={<SharedGuide />} />
            <Route path="/research/:slug" element={<SharedResearch />} />
            <Route path="/chat/shared/:token" element={<SharedChat />} />
            <Route path="/workspace" element={<Hub />} />
            {/* Legacy redirects: keep old shared links working */}
            <Route path="/guide" element={<RedirectGuideToLearn />} />
            <Route path="/guide/:slug" element={<RedirectGuideToLearn />} />
            <Route path="/hub" element={<RedirectHubToWorkspace />} />
            <Route path="/hack" element={<RedirectHackToWorkspace />} />
            <Route path="/go/:slug" element={<Go />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/security" element={<Security />} />
            
            <Route path="/about" element={<About />} />
            <Route path="/usage-limits" element={<UsageLimits />} />
            <Route path="/conceptually" element={<Conceptually />} />
            <Route path="/accelerator" element={<Accelerator />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/account" element={<Account />} />
            <Route path="/c/:token" element={<SharedCollection />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/quests" element={<Play />} />
            <Route path="/quests/paths/:slug" element={<PlayPath />} />
            <Route path="/quests/paths/:pathSlug/quests/:questSlug" element={<QuestWorkshopPage />} />
            {/* Legacy /play and /build → /quests redirects (route renamed twice) */}
            <Route path="/play/*" element={<RedirectLegacyToQuests />} />
            <Route path="/play" element={<Navigate to="/quests" replace />} />
            <Route path="/build/*" element={<RedirectLegacyToQuests />} />
            <Route path="/build" element={<Navigate to="/quests" replace />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/invite" element={<Invite />} />
            <Route path="/invite/dashboard" element={<InviteDashboard />} />
            <Route path="/profile/:username" element={<Profile />} />
            <Route path="/invite/:code" element={<InviteAccept />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/squads" element={<Squads />} />
            <Route path="/squads/join/:code" element={<SquadJoin />} />
            <Route path="/squads/:slug" element={<SquadDetail />} />
            <Route path="/squad-rooms" element={<SquadRooms />} />
            <Route path="/squad-rooms/:id" element={<SquadRoom />} />
            <Route path="/library" element={<Library />} />
            <Route path="/library/wishlist" element={<LibraryRoadmap />} />
            {/* Legacy /library/roadmap → /library/wishlist */}
            <Route path="/library/roadmap" element={<RedirectLibraryRoadmap />} />
            <Route path="/library/artifact/:id" element={<LibraryArtifact />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/projects/leaderboard" element={<ProjectsLeaderboard />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/challenges" element={<Challenges />} />
            <Route path="/roadmap" element={<Mastery />} />
            <Route path="/mastery/history" element={<MasteryHistory />} />
            <Route path="/hackathon" element={<Hackathon />} />
            <Route path="/admin/hackathon" element={<AdminHackathon />} />
            {/* Legacy /mastery → /roadmap redirect (route was renamed) */}
            <Route path="/mastery" element={<RedirectMasteryToRoadmap />} />
            <Route path="/power21" element={<Power21 />} />
            <Route path="/challenges/mastery" element={<MasterChallenges />} />
            {/* Legacy /master-challenges → /challenges/mastery */}
            <Route path="/master-challenges" element={<RedirectMasterChallenges />} />
            <Route path="/cert/:code" element={<Cert />} />
            <Route path="/admin/affiliates" element={<AdminAffiliates />} />
            <Route path="/admin/tools" element={<AdminTools />} />
            <Route path="/admin/feedback" element={<AdminFeedback />} />
            <Route path="/admin/errors" element={<AdminErrors />} />
            <Route path="/admin/abuse" element={<AdminAbuse />} />
            <Route path="/admin/security" element={<AdminSecurity />} />
            <Route path="/admin/security/issues" element={<AdminSecurityIssues />} />
            <Route path="/admin/site" element={<AdminSite />} />
            <Route path="/admin/funnel" element={<AdminFunnel />} />
            <Route path="/admin/billing" element={<AdminBilling />} />
            <Route path="/admin/project-skills" element={<AdminProjectSkills />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
            <Route path="/admin/lighthouse" element={<AdminLighthouse />} />
            <Route path="/builder" element={<Builder />} />
            <Route path="/sites/:slug" element={<PublishedSite />} />
            {/* /agents — dashboard listing my saved agents (search +
                empty state). Deep links with ?goal=…&autorun=1 are
                forwarded to /agents/builder so external callers
                (chat playbooks, library entries) keep working. */}
            <Route path="/agents" element={<AgentsDashboard />} />
            <Route path="/agents/builder" element={<Agents />} />
            {/* /planner — Easy Mode Task Planner. Surfaced as a tab
                from /workspace; standalone route lets users deep-link. */}
            <Route path="/planner" element={<Planner />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </ErrorBoundary>
          </Suspense>
          </MaintenanceGate>
          <MobileBottomNav />
          <CompareTrayBar />
          <ReportBugButton />
          <AskTutorFAB />
          <OnboardingModal />
          <OperatorMilestoneModal />
          <Level2PaywallGate />
          <FirstOutputSignupGate />
          <FirstVisitSignupModal />
          <BeginnerShellWithBoundary />
          <AgentUnlockModal />
          <FirstAgentPeekModal />
          <FirstExitCoachmarks />
          </CompareTrayProvider>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
